import { Link } from "react-router-dom";
import { CalendarCheck, CheckCircle2, Phone, Repeat } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import ContractorForm from "../components/forms/ContractorForm";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import { BUSINESS, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/api";

const TRADES = ["Roofers", "Remodelers", "Demolition Companies", "Restoration Companies", "Landscapers", "New Construction", "Other Construction Trades"];

const WITH_YWS = [
  "Same-Day Swaps Available",
  "Saturday Swaps Available",
  "Long-term rentals — no maximum rental period",
  "Usage-based pricing for high-volume accounts",
  "Transparent pricing, no hidden fees",
  "Locally owned & veteran owned",
  "Real people you can reach",
];

export default function Contractors() {
  return (
    <>
      <SEO
        title="Contractor Dumpster Rentals"
        description="Dumpster service built for general contractors in Yavapai County. Long-term roll-off rentals, Same-Day Swaps Available, and pricing built around how you use us."
        path="/contractor-dumpster-rentals"
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Contractor Dumpster Rental",
          provider: { "@type": "LocalBusiness", name: BUSINESS.name, telephone: BUSINESS.phone },
          areaServed: SERVICE_AREAS.map((a) => a.name),
        }}
      />

      <section className="bg-ink text-white" data-testid="contractors-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 sm:pb-20">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contractor Dumpster Rentals" }]} />
          <div className="mt-8 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.18em] text-sky-400 mb-4">For General Contractors</p>
              <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
                Dumpster Service Built for <span className="text-sky-400">General Contractors</span>
              </h1>
              <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                Keep jobs moving with long-term roll-off rentals, Same-Day Swaps Available, Saturday Swaps Available, and pricing built around how often you use us.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="#pricing" className="px-8 py-4 rounded-lg bg-sky-600 hover:bg-sky-500 text-center font-heading font-semibold transition-all active:scale-[0.98]" data-testid="contractors-hero-cta">
                  Get Contractor Pricing
                </a>
                <a href={BUSINESS.phoneHref} onClick={() => track("click_to_call", { location: "contractors_hero" })} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border-2 border-slate-600 hover:border-sky-400 font-heading font-semibold transition-all" data-testid="contractors-hero-call">
                  <Phone className="h-4 w-4" /> {BUSINESS.phone}
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -inset-3 bg-sky-600/10 rounded-2xl rotate-1" aria-hidden="true" />
              <img src="/assets/yws/uploads/jobsite-new-construction.jpeg" alt="YWS roll-off and front-load dumpsters at an active new construction site" className="relative rounded-2xl w-full object-cover aspect-[4/3] shadow-2xl" data-testid="contractors-hero-image" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white" data-testid="contractors-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">What You Get With YWS</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Your dumpster company shouldn't be the reason your crew is standing around. Here's how we run every contractor account:
            </p>
            <ul className="mt-6 space-y-3">
              {WITH_YWS.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-sky-600 mt-0.5 shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-7">
              <h3 className="font-heading font-bold text-xl text-slate-900">Pricing Built Around How You Actually Use Us</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                A contractor operating multiple job sites and requesting several swaps each week has very different needs than someone renting one dumpster for one project. We don't force every customer into the same pricing structure.
              </p>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                High-volume customers who keep dumpsters long-term and request frequent swaps can receive pricing structured around their usage. No surprise fees. No games. Straightforward pricing.
              </p>
              <p className="mt-4 font-heading font-semibold text-sky-700 text-sm">The more you use us, the better we can work with you.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 bg-slate-50 border-y border-slate-200" data-testid="contractors-trades">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Not a GC? No Problem.</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">We work with every kind of high-volume and recurring dumpster customer:</p>
          <div className="mt-7 flex flex-wrap justify-center gap-2.5">
            {TRADES.map((t) => (
              <span key={t} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm font-medium text-slate-700" data-testid={`trade-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-16 sm:py-20 bg-white scroll-mt-20" data-testid="contractors-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-8">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">Get Contractor Pricing</h2>
            <p className="mt-3 text-slate-600">Tell us how your operation runs. We'll build pricing around it.</p>
          </Reveal>
          <ContractorForm />
        </div>
      </section>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion page="contractors" />
        </div>
      </section>

      <CTASection title="Keep Your Job Sites Moving" subtitle="Long-term rentals, fast swaps, and a local team that answers. Let's talk about your operation." />
    </>
  );
}
